import {
    Component,
    DestroyRef,
    inject,
    OnInit,
    signal,
    WritableSignal
} from '@angular/core';
import { finalize, merge, of } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { debounceTime, delay, map, switchMap } from 'rxjs/operators';

/**
 * libs
 */
import { ToastrService } from 'ngx-toastr';
import { NgSelectModule } from '@ng-select/ng-select';
import { GoogleMap, MapMarker } from '@angular/google-maps';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

/***
 * interfaces
 */
import { IFacilityDto } from './interfaces';

/**
 * consts
 */
import { FacilityConst, FacilityTranslateMapper } from './consts';

/**
 * services
 */
import { FacilityService, FacilityFormService } from './services';

/**
 * components & directives
 */
import { ButtonDirective } from '@sharedDirectives/button';
import { InputFieldComponent } from '@sharedModules/forms/components/input-field/input-field.component';
import { SpinKitWaveComponent } from '@shared/components/spinner/spinkit/components/spin-kit-wave/spin-kit-wave.component';

/**
 * Facility view component.
 * Loads facility data on init, displays a full edit form with Google Map integration,
 * and submits changes via PUT /facilities/:id.
 */
@Component({
    selector: 'app-facility',
    templateUrl: './facility.component.html',
    styleUrl: './facility.component.scss',
    imports: [
        ReactiveFormsModule,
        /**
         * libs
         */
        MapMarker,
        GoogleMap,
        TranslatePipe,
        NgSelectModule,
        /**
         * directives
         */
        ButtonDirective,
        /**
         * components
         */
        InputFieldComponent,
        SpinKitWaveComponent,
    ],
})
export class FacilityComponent implements OnInit {

    // ─── Services ────────────────────────────────────────────────────────────

    private readonly _facilityService  = inject(FacilityService);
    private readonly _formService      = inject(FacilityFormService);
    private readonly _toastr           = inject(ToastrService);
    private readonly _route            = inject(ActivatedRoute);
    private readonly _translate        = inject(TranslateService);
    private readonly _destroyRef       = inject(DestroyRef);

    // ─── Public state ─────────────────────────────────────────────────────────

    /**
     * Facility module constants — exposed to the template.
     */
    public readonly FACILITY_CONST = FacilityConst;

    /**
     * Translate key mapper — all keys are functions to re-evaluate on language switch.
     */
    public readonly TRANSLATE_KEY_MAP = FacilityTranslateMapper;

    /**
     * Reactive form group from the form service.
     */
    public get facilityForm() { 
        return this._formService.facilityForm;
    }

    /**
     * Facility types lookup signal — sourced via FacilityService from the global LookupService.
     */
    public readonly facilityTypesLookup = this._facilityService.facilityTypesLookup;

    /**
     * Loading state signal — true while any HTTP call is in flight.
     */
    public readonly isLoading: WritableSignal<boolean> = signal(false);

    /**
     * Google Map center — updated when form lat/lng values change.
     */
    public readonly mapCenter: WritableSignal<google.maps.LatLngLiteral> = signal<google.maps.LatLngLiteral>({
        lat: 0,
        lng: 0
    });

    /**
     * Draggable marker position — kept in sync with mapCenter.
     */
    public readonly markerPosition: WritableSignal<google.maps.LatLngLiteral> = signal<google.maps.LatLngLiteral>({
        lat: 0,
        lng: 0
    });

    /**
     * Google Map display options — shows only fullscreen and zoom controls.
     */
    public readonly mapOptions: google.maps.MapOptions = {
        fullscreenControl: true,
        zoomControl: true,
        mapTypeControl: false,
        streetViewControl: false,
        rotateControl: false,
        scaleControl: false,
        draggable: true
    };

    /**
     * get facility details from data route
     */
    public get facilityDetails(): IFacilityDto {
        return this._route.snapshot.data['facility']
    }

    /**
     * get facility Id
     */
    public get facilityId(): string {
        return this.facilityDetails.id ?? ''
    }

    // ─── Lifecycle ────────────────────────────────────────────────────────────

    /** @lifecycle */
    public ngOnInit(): void {
        this._formService.patchForm(this.facilityDetails);
        this._syncMapFromForm();
        this._watchCoordinates();
    }

    // ─── Public actions ───────────────────────────────────────────────────────

    /**
     * Called when the map marker is dragged to a new position.
     * Updates the latitude and longitude form controls to match.
     * @param event Google Maps drag-end mouse event.
     */
    public onMarkerDragEnd(event: google.maps.MapMouseEvent): void {
        this._applyMapPosition(event);
    }

    /**
     * Called when the user clicks anywhere on the map.
     * Moves the marker and updates the form coordinates — does NOT re-center the map.
     * @param event Google Maps click mouse event.
     */
    public onMapClick(event: google.maps.MapMouseEvent): void {
        this._applyMapPosition(event);
    }

    /**
     * Validates the form and submits a PUT request with the current form values.
     * Marks all controls as touched to reveal validation errors if the form is invalid.
     */
    public submit(): void {

        // void
        if (this.facilityForm.invalid) {
            this.facilityForm.markAllAsTouched();
            return;
        }

        // start loading
        this.isLoading.set(true);

        // get value
        const payload = this._formService.buildPayload();

        // update
        this._facilityService.updateFacility(this.facilityId, payload)
            .pipe(finalize(() => this.isLoading.set(false)))
            .subscribe({
                next: () => {
                    this.facilityForm.markAsPristine();
                    // this._toastr.success(
                    //     this._translate.instant('HTTP.RESPONSE.UPDATED'),
                    //     this._translate.instant('TOAST.SUCCESS'),
                    // );
                },
            });
    }

    /**
     * Reverts the form to the last loaded facility data.
     * Cancel button is disabled when the form is pristine.
     */
    public cancel(): void {
        if (this.facilityDetails) {
            this._formService.patchForm(this.facilityDetails);
            this._syncMapFromForm();
        }
    }

    // ─── Private helpers ──────────────────────────────────────────────────────

    /**
     * combine latLng in one block
     */
    private _getLatLngFormControl(): { lat: FormControl, lng: FormControl } {
        return {
            lat: this.facilityForm.controls['latitude'],
            lng: this.facilityForm.controls['longitude'],
        }
    }    

    /**
     * Subscribes to latitude and longitude value changes and updates the map position.
     * Uses debounce to avoid updating on every keystroke.
     */
    private _watchCoordinates(): void {
        
        // ES6 Destruction & rename
        const { lat: latControl, lng: lngControl } = this._getLatLngFormControl();
        
        const lat$ = latControl!.valueChanges;
        const lng$ = lngControl!.valueChanges;
        merge(lat$, lng$)
            .pipe(
                delay(100), // delay for insure controls status is updated
                map(_ => !(latControl.pristine && latControl.pristine)), // map to check if controls is pristine
                // if controls is pristine skip debounceTime else set it
                switchMap((status) => {
                    return of(status).pipe(debounceTime(status ? 0 : 500))
                }),
                takeUntilDestroyed(this._destroyRef)
            )
            .subscribe((centerUpdateStatus) => this._syncMapFromForm(centerUpdateStatus));
    }

    /**
     * Extracts lat/lng from a Google Maps mouse event, patches the form, and moves the marker.
     * Shared by drag-end and map-click handlers.
     * @param event Google Maps mouse event carrying a latLng property.
     */
    private _applyMapPosition(event: google.maps.MapMouseEvent): void {

        // void
        if (event.latLng == undefined) return;

        // get latLng
        const lat = event.latLng.lat();
        const lng = event.latLng.lng();

        // update form
        this.facilityForm.patchValue({ latitude: lat.toString(), longitude: lng.toString()});
        this.facilityForm.markAsDirty();

        // get lat lng controls
        const latControl = this._getLatLngFormControl().lat;
        const lngControl = this._getLatLngFormControl().lng;

        // update Pristine
        latControl?.markAsPristine();
        lngControl?.markAsPristine();
        
    }

    /**
     * Reads lat/lng from the form and updates the map center and marker position.
     * @param [centerUpdate=true] if `true` update center position, else not update
     */
    private _syncMapFromForm(centerUpdate: boolean = true): void {
        
        // ES6 Destruction & rename
        const { lat: latControl, lng: lngControl } = this._getLatLngFormControl();

        const lat = parseFloat(latControl?.value ?? '');
        const lng = parseFloat(lngControl?.value ?? '');
        
        // void
        if (isNaN(lat) && isNaN(lng)) return;

        // get latLng
        const position = { lat, lng };
        
        // check with sync if we need update center position or no
        if (centerUpdate) {
            this.mapCenter.set(position);
        }

        // update marker position
        this.markerPosition.set(position);

    }

}
